import { supabase } from '@/shared/lib/supabase';

/**
 * adminCronService — staff-side cron health.
 *
 * Wraps `admin_cron_health()` + `admin_cron_trigger()` SECURITY DEFINER
 * functions (Sprint Admin-A A-3 migration). Cron schema isn't exposed to
 * authenticated, so these functions are the only path from the browser.
 */

export interface CronHealthRow {
  jobid: number;
  jobname: string;
  schedule: string;
  active: boolean;
  last_run_start: string | null;
  last_run_end: string | null;
  last_run_status: string | null;
  last_run_error: string | null;
  next_run_at: string | null;
  recent_5_runs: Array<{
    start_time: string;
    end_time: string | null;
    status: string;
    return_message: string;
  }>;
  in_allowlist: boolean;
}

/**
 * A row of `public.v_cron_liveness` (via the staff-gated `v_cron_health`).
 *
 * F4b (2026-08-18) — this is the TRUSTWORTHY signal, and it is a different
 * thing from `CronHealthRow`:
 *
 *   CronHealthRow    reads cron.job_run_details -> "the HTTP gateway replied"
 *   CronLivenessRow  reads heartbeats + audit   -> "the function actually ran"
 *
 * Per CLAUDE.md §4.8, `job_run_details.status = 'succeeded'` is recorded even
 * when the gateway answered 401 or 500 — which is exactly why the 2026-06-02
 * (16-day) and 2026-07-04 (18-day) billing outages were invisible. This page
 * showed only the first signal until 2026-08-18.
 *
 * `last_seen` is GREATEST(audit_log event, cron_heartbeat.last_beat_at): daily
 * jobs write an audit row, minute runners beat instead (see _shared/cronBeat.ts).
 */
export interface CronLivenessRow {
  job_name: string;
  audit_event: string;
  max_age_hours: number;
  active: boolean;
  note: string | null;
  last_seen: string | null;
  hours_since: number | null;
  is_stale: boolean;
}

export const adminCronService = {
  async list(): Promise<CronHealthRow[]> {
    const { data, error } = await supabase.rpc('admin_cron_health');
    if (error) throw new Error(`Could not load cron health: ${error.message}`);
    return (data ?? []) as CronHealthRow[];
  },

  /**
   * Heartbeat liveness per registered job. Not every pg_cron job has one —
   * in-database maintenance jobs (the watchdog itself, the run-details prune)
   * are deliberately unregistered, so callers must treat "no row" as
   * "not monitored", never as "dead".
   */
  async liveness(): Promise<CronLivenessRow[]> {
    const { data, error } = await supabase
      .from('v_cron_health')
      .select('job_name, audit_event, max_age_hours, active, note, last_seen, hours_since, is_stale');
    if (error) throw new Error(`Could not load cron liveness: ${error.message}`);
    return (data ?? []) as CronLivenessRow[];
  },

  async trigger(jobname: string): Promise<{ ok: boolean; note: string }> {
    const { data, error } = await supabase.rpc('admin_cron_trigger', { p_jobname: jobname });
    if (error) throw new Error(error.message);
    return data as { ok: boolean; note: string };
  },
};
